/**
 * Today view: the day form.
 *
 * One record per date. Day type and location at the top, then what was done,
 * what is planned, what you are waiting on, expenses and free notes. Everything
 * autosaves; the header shows a brief "Saved".
 *
 * Quick entry: pressing Enter in a done or planned line opens a new line below
 * it, and each line keeps its own workstream, defaulting to the last one used.
 */

import { button, clear, el, field, icon, select, toast } from '../lib/dom.js';
import { addDays, formatDate, formatDateLong, isValidKey, relativeDays, today } from '../lib/dates.js';
import { DAY_TYPES, LOCATIONS, daysOpen, uid } from '../lib/model.js';
import * as store from '../lib/store.js';
import { createAutosave } from '../lib/autosave.js';
import { downloadBackup } from '../lib/export.js';

/** The workstream a new line should default to: the last one touched. */
let lastWorkstream = null;

function rememberWorkstream(value) {
  if (value) {
    lastWorkstream = value;
    try {
      localStorage.setItem('timesheet.lastWorkstream', value);
    } catch {
      // Private browsing can refuse localStorage; the in-memory value still works.
    }
  }
}

function defaultWorkstream(settings) {
  if (lastWorkstream) return lastWorkstream;
  try {
    const stored = localStorage.getItem('timesheet.lastWorkstream');
    if (stored) {
      lastWorkstream = stored;
      return stored;
    }
  } catch {
    /* ignore */
  }
  return settings.workstreams[0] || '';
}

export async function renderToday(container, params, app) {
  const date = isValidKey(params.date) ? params.date : today();
  const settings = await store.getSettings();

  // Opening today creates the record, carrying forward unfinished planned items
  // and open waiting-on items from the last day worked. Other dates are only
  // created once you actually type something.
  const day =
    date === today() ? (await store.ensureDay(date)).day : await store.getDayOrBlank(date);

  const save = createAutosave(() => store.saveDay(day));
  app.setPendingSave(save);

  clear(container);
  container.append(renderDayNav(date, app));

  if (date === today()) {
    const reminder = await store.backupReminder();
    if (reminder) container.append(renderBackupNotice(reminder, app));
  }

  container.append(
    renderDayBasics(day, settings, save),
    renderEntrySection({
      title: 'Done',
      hint: 'What you actually worked on. Press Enter for a new line.',
      day,
      settings,
      save,
      listName: 'done',
    }),
    renderEntrySection({
      title: 'Planned next',
      hint: 'Tick items off as they are finished. Anything left open rolls over to the next day.',
      day,
      settings,
      save,
      listName: 'planned',
    }),
    renderWaitingOn(day, save),
    renderExpenses(day, settings, save),
    renderNotes(day, save),
  );
}

// --- Day navigation ---------------------------------------------------------

function renderDayNav(date, app) {
  const picker = el('input', {
    type: 'date',
    class: 'input',
    value: date,
    'aria-label': 'Choose a date',
    onchange: (event) => {
      if (isValidKey(event.target.value)) app.go(`#/today/${event.target.value}`);
    },
  });

  return el('div', { class: 'daynav' }, [
    button(icon('prev'), {
      class: 'btn btn--icon',
      'aria-label': 'Previous day',
      title: 'Previous day',
      onclick: () => app.go(`#/today/${addDays(date, -1)}`),
    }),
    button(icon('next'), {
      class: 'btn btn--icon',
      'aria-label': 'Next day',
      title: 'Next day',
      onclick: () => app.go(`#/today/${addDays(date, 1)}`),
    }),
    el('div', { class: 'daynav__date' }, [
      el('h2', { class: 'daynav__title' }, formatDateLong(date)),
      el('p', { class: 'daynav__sub' }, date === today() ? 'Today' : relativeDays(date)),
    ]),
    picker,
    date === today()
      ? null
      : button('Today', { class: 'btn btn--small', onclick: () => app.go('#/today') }),
  ]);
}

function renderBackupNotice(reminder, app) {
  const message = reminder.never
    ? `You have ${reminder.dayCount} ${reminder.dayCount === 1 ? 'day' : 'days'} recorded and have not taken a backup yet.`
    : `Your last backup was ${reminder.days} days ago.`;

  // Without persistent storage the browser may clear the data, so say so —
  // it explains why the reminder arrives sooner.
  const context = reminder.persisted
    ? ' Everything lives on this device only.'
    : ' Everything lives on this device only, and this browser has not granted persistent storage, so it could be cleared.';

  return el('div', { class: 'notice', role: 'status' }, [
    el('p', message + context),
    button('Back up now', {
      class: 'btn btn--primary btn--small',
      onclick: async (event) => {
        event.target.disabled = true;
        await downloadBackup();
        toast('Backup downloaded.');
        app.refresh();
      },
    }),
  ]);
}

// --- Day type, location -----------------------------------------------------

function renderDayBasics(day, settings, save) {
  const name = `dayType-${day.date}`;
  const options = DAY_TYPES.map((type) =>
    el('label', { class: 'segmented__option' }, [
      el('input', {
        type: 'radio',
        name,
        value: type,
        checked: day.dayType === type,
        onchange: () => {
          day.dayType = type;
          save.now();
        },
      }),
      el('span', type),
    ]),
  );

  const otherField = el('div', { class: 'field', hidden: day.location !== 'Other' }, [
    el('label', { class: 'field__label', for: `locationOther-${day.date}` }, 'Where exactly'),
    el('input', {
      id: `locationOther-${day.date}`,
      class: 'input',
      type: 'text',
      value: day.locationOther,
      placeholder: 'For example, client head office',
      oninput: (event) => {
        day.locationOther = event.target.value;
        save.schedule();
      },
    }),
  ]);

  const locationSelect = select(LOCATIONS, day.location, {
    'aria-label': 'Location',
    onchange: (event) => {
      day.location = event.target.value;
      otherField.hidden = day.location !== 'Other';
      save.now();
    },
  });

  return el('section', { class: 'card' }, [
    el('fieldset', { class: 'segmented' }, [el('legend', 'Day type'), ...options]),
    el('div', { class: 'grid-2 day-basics' }, [field('Location', locationSelect), otherField]),
  ]);
}

// --- Done and planned lists -------------------------------------------------

/**
 * Renders a list of text entries with a workstream picker on each line.
 *
 * The list always ends with one blank line so you can start typing straight
 * away; blank lines are stripped before saving.
 */
function renderEntrySection({ title, hint, day, settings, save, listName }) {
  const isPlanned = listName === 'planned';
  const list = el('div', { class: 'entries' });

  const ensureTrailingBlank = () => {
    const entries = day[listName];
    if (!entries.length || entries[entries.length - 1].text.trim()) {
      const entry = newEntry(settings, isPlanned);
      entries.push(entry);
      list.append(makeRow(entry));
    }
  };

  /**
   * Blank placeholder lines follow the last workstream used, so whatever you
   * type next is already filed against the workstream you are working in.
   */
  const syncBlankLines = () => {
    for (const row of list.children) {
      const rowEntry = row.entryRef;
      if (!rowEntry || rowEntry.text.trim() || !lastWorkstream) continue;
      rowEntry.workstream = lastWorkstream;
      if (row.workstreamSelect) row.workstreamSelect.value = lastWorkstream;
    }
  };

  const removeEntry = (entry, row) => {
    const entries = day[listName];
    const index = entries.indexOf(entry);
    if (index >= 0) entries.splice(index, 1);
    row.remove();
    ensureTrailingBlank();
    save.now();
  };

  /** Insert a fresh line after `entry` and put the cursor in it. */
  const insertAfter = (entry) => {
    const entries = day[listName];
    const index = entries.indexOf(entry);
    const fresh = newEntry(settings, isPlanned);
    entries.splice(index + 1, 0, fresh);
    const row = makeRow(fresh);
    const existingRows = [...list.children];
    if (existingRows[index]) existingRows[index].after(row);
    else list.append(row);
    row.querySelector('input[type="text"]').focus();
  };

  function makeRow(entry) {
    const text = el('input', {
      type: 'text',
      class: 'input',
      value: entry.text,
      placeholder: isPlanned ? 'Something to do next' : 'Something you did',
      'aria-label': isPlanned ? 'Planned item' : 'Work done',
      oninput: (event) => {
        entry.text = event.target.value;
        // Once a carried line is reworded it is no longer simply rolled over.
        if (entry.carried && row) {
          entry.carried = false;
          row.classList.remove('entry--carried');
          const tag = row.querySelector('.entry__tag');
          if (tag) tag.remove();
        }
        ensureTrailingBlank();
        save.schedule();
      },
      onkeydown: (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (!entry.text.trim()) return;
          insertAfter(entry);
          save.schedule();
        }
      },
      onblur: () => save.schedule(),
    });

    const workstream = select(settings.workstreams, entry.workstream, {
      class: 'input entry__workstream',
      'aria-label': 'Workstream',
      onchange: (event) => {
        entry.workstream = event.target.value;
        rememberWorkstream(entry.workstream);
        syncBlankLines();
        save.now();
      },
    });

    const textWrap = el('div', { class: 'entry__text' }, [text]);

    const children = [];
    if (isPlanned) {
      children.push(
        el('span', { class: 'entry__check' }, [
          el('input', {
            type: 'checkbox',
            checked: entry.done,
            'aria-label': 'Mark as done',
            onchange: (event) => {
              entry.done = event.target.checked;
              row.classList.toggle('entry--ticked', entry.done);
              save.now();
            },
          }),
        ]),
      );
    }
    children.push(
      textWrap,
      workstream,
      button(icon('remove'), {
        class: 'btn btn--quiet btn--icon',
        'aria-label': 'Remove this line',
        title: 'Remove this line',
        onclick: () => removeEntry(entry, row),
      }),
    );

    const row = el(
      'div',
      {
        class: [
          'entry',
          isPlanned ? '' : 'entry--done-list',
          entry.carried ? 'entry--carried' : '',
          entry.done ? 'entry--ticked' : '',
        ]
          .filter(Boolean)
          .join(' '),
      },
      children,
    );

    if (entry.carried) {
      textWrap.append(el('span', { class: 'entry__tag', title: 'Rolled over from the previous day' }, 'carried'));
    }

    // Kept on the row so syncBlankLines can reach them without a re-render.
    row.entryRef = entry;
    row.workstreamSelect = workstream;
    return row;
  }

  for (const entry of day[listName]) list.append(makeRow(entry));
  ensureTrailingBlank();

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h2', { class: 'card__title' }, title),
      el('p', { class: 'card__hint' }, hint),
    ]),
    list,
    el('div', { class: 'entry-foot' }, [
      button('Add line', {
        class: 'btn btn--small',
        onclick: () => {
          ensureTrailingBlank();
          const inputs = list.querySelectorAll('input[type="text"]');
          if (inputs.length) inputs[inputs.length - 1].focus();
        },
      }),
    ]),
  ]);
}

function newEntry(settings, isPlanned) {
  const base = { id: uid(), text: '', workstream: defaultWorkstream(settings) };
  return isPlanned ? { ...base, done: false, carried: false } : base;
}

// --- Waiting on -------------------------------------------------------------

function renderWaitingOn(day, save) {
  const list = el('div', { class: 'waiting' });

  const empty = el('p', { class: 'empty' }, 'Nothing outstanding.');

  const refreshEmpty = () => {
    empty.hidden = day.waitingOn.length > 0;
  };

  function makeRow(item) {
    const age = daysOpen(item, day.date);
    const ageLabel = item.clearedDate
      ? `cleared ${formatDate(item.clearedDate)}`
      : `${age} ${age === 1 ? 'day' : 'days'} open`;

    const row = el(
      'div',
      { class: `waiting__item${item.clearedDate ? ' waiting__item--cleared' : ''}` },
      [
        el('input', {
          type: 'text',
          class: 'input',
          value: item.who,
          placeholder: 'Who',
          'aria-label': 'Waiting on who',
          oninput: (event) => {
            item.who = event.target.value;
            save.schedule();
          },
          onblur: () => save.schedule(),
        }),
        el('input', {
          type: 'text',
          class: 'input waiting__what',
          value: item.what,
          placeholder: 'What you need from them',
          'aria-label': 'What you are waiting for',
          oninput: (event) => {
            item.what = event.target.value;
            save.schedule();
          },
          onblur: () => save.schedule(),
        }),
        el(
          'span',
          {
            class: `waiting__age${!item.clearedDate && age >= 7 ? ' waiting__age--stale' : ''}`,
            title: `Raised ${formatDate(item.sinceDate)}`,
          },
          ageLabel,
        ),
        button(item.clearedDate ? 'Reopen' : 'Clear', {
          class: 'btn btn--small',
          onclick: () => {
            item.clearedDate = item.clearedDate ? '' : day.date;
            const replacement = makeRow(item);
            row.replaceWith(replacement);
            save.now();
          },
        }),
        button(icon('remove'), {
          class: 'btn btn--quiet btn--icon',
          'aria-label': 'Remove this item',
          title: 'Remove this item',
          onclick: () => {
            const index = day.waitingOn.indexOf(item);
            if (index >= 0) day.waitingOn.splice(index, 1);
            row.remove();
            refreshEmpty();
            save.now();
          },
        }),
      ],
    );
    return row;
  }

  for (const item of day.waitingOn) list.append(makeRow(item));
  refreshEmpty();

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h2', { class: 'card__title' }, 'Waiting on'),
      el('p', { class: 'card__hint' }, 'Carried forward each day until you clear it.'),
    ]),
    empty,
    list,
    el('div', { class: 'entry-foot' }, [
      button('Add item', {
        class: 'btn btn--small',
        onclick: () => {
          const item = { id: uid(), who: '', what: '', sinceDate: day.date, clearedDate: '' };
          day.waitingOn.push(item);
          const row = makeRow(item);
          list.append(row);
          refreshEmpty();
          row.querySelector('input').focus();
        },
      }),
    ]),
  ]);
}

// --- Expenses ---------------------------------------------------------------

function renderExpenses(day, settings, save) {
  const list = el('div', { class: 'expenses' });
  const empty = el('p', { class: 'empty' }, 'No expenses today.');
  const labels = el('div', { class: 'expense__labels', 'aria-hidden': 'true' }, [
    el('span', 'Category'),
    el('span', 'Amount (£)'),
    el('span', 'Distance (km)'),
    el('span', 'Note'),
    el('span', 'Receipt ref.'),
    el('span', ''),
  ]);

  // The column headings only make sense once there is a row beneath them.
  const refreshEmpty = () => {
    empty.hidden = day.expenses.length > 0;
    labels.hidden = day.expenses.length === 0;
  };

  function makeRow(expense) {
    const row = el('div', { class: 'expense' }, [
      select(settings.expenseCategories, expense.category, {
        class: 'input expense__category',
        'aria-label': 'Expense category',
        onchange: (event) => {
          expense.category = event.target.value;
          save.now();
        },
      }),
      el('input', {
        type: 'number',
        inputmode: 'decimal',
        step: '0.01',
        min: '0',
        class: 'input expense__amount',
        value: expense.amount || '',
        placeholder: '0.00',
        'aria-label': 'Amount in pounds',
        oninput: (event) => {
          expense.amount = event.target.value === '' ? 0 : Number(event.target.value);
          save.schedule();
        },
        onblur: () => save.schedule(),
      }),
      el('input', {
        type: 'number',
        inputmode: 'decimal',
        step: '1',
        min: '0',
        class: 'input expense__km',
        value: expense.km === '' ? '' : expense.km,
        placeholder: 'km',
        'aria-label': 'Distance in kilometres, for fuel',
        oninput: (event) => {
          expense.km = event.target.value === '' ? '' : Number(event.target.value);
          save.schedule();
        },
        onblur: () => save.schedule(),
      }),
      el('input', {
        type: 'text',
        class: 'input expense__note',
        value: expense.note,
        placeholder: 'Note',
        'aria-label': 'Expense note',
        oninput: (event) => {
          expense.note = event.target.value;
          save.schedule();
        },
        onblur: () => save.schedule(),
      }),
      el('input', {
        type: 'text',
        class: 'input expense__receipt',
        value: expense.receiptRef,
        placeholder: 'Receipt ref.',
        'aria-label': 'Receipt reference',
        oninput: (event) => {
          expense.receiptRef = event.target.value;
          save.schedule();
        },
        onblur: () => save.schedule(),
      }),
      button(icon('remove'), {
        class: 'btn btn--quiet btn--icon expense__remove',
        'aria-label': 'Remove this expense',
        title: 'Remove this expense',
        onclick: () => {
          const index = day.expenses.indexOf(expense);
          if (index >= 0) day.expenses.splice(index, 1);
          row.remove();
          refreshEmpty();
          save.now();
        },
      }),
    ]);
    return row;
  }

  for (const expense of day.expenses) list.append(makeRow(expense));
  refreshEmpty();

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h2', { class: 'card__title' }, 'Expenses'),
      el('p', { class: 'card__hint' }, 'Distance is only needed for fuel.'),
    ]),
    empty,
    labels,
    list,
    el('div', { class: 'entry-foot' }, [
      button('Add expense', {
        class: 'btn btn--small',
        onclick: () => {
          const expense = {
            id: uid(),
            category: settings.expenseCategories[0] || '',
            amount: 0,
            km: '',
            note: '',
            receiptRef: '',
          };
          day.expenses.push(expense);
          const row = makeRow(expense);
          list.append(row);
          refreshEmpty();
          row.querySelector('input').focus();
        },
      }),
    ]),
  ]);
}

// --- Notes ------------------------------------------------------------------

function renderNotes(day, save) {
  const textarea = el('textarea', {
    class: 'textarea',
    placeholder: 'Anything else worth remembering about today.',
    oninput: (event) => {
      day.notes = event.target.value;
      save.schedule();
    },
    onblur: () => save.schedule(),
  });
  textarea.value = day.notes;

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [el('h2', { class: 'card__title' }, 'Notes')]),
    field('', textarea),
  ]);
}
